import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://yabhmldscqdbensejfhi.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlhYmhtbGRzY3FkYmVuc2VqZmhpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjIyNjgzMDEsImV4cCI6MjA3Nzg0NDMwMX0.sr6SrK22DcTzfxVsf20KVe0mJpiMC-SdJ5M2hlRs5a4';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database helper functions
export const db = {
  // Memories
  async saveMemory(memory) {
    const { data, error } = await supabase
      .from('memories')
      .insert([{
        title: memory.title,
        content: memory.content,
        property_id: memory.propertyId,
        property_name: memory.propertyName,
        category: memory.category,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getMemories(propertyId = null) {
    let query = supabase
      .from('memories')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (propertyId) {
      query = query.eq('property_id', propertyId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async searchMemories(searchTerm) {
    const { data, error } = await supabase
      .from('memories')
      .select('*')
      .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async deleteMemory(id) {
    const { error } = await supabase
      .from('memories')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  // Timeline Events
  async saveTimelineEvent(event) {
    const { data, error } = await supabase
      .from('timeline_events')
      .insert([{
        year: event.year,
        title: event.title,
        description: event.desc || event.description,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getTimelineEvents() {
    const { data, error } = await supabase
      .from('timeline_events')
      .select('*')
      .order('year', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  // Voice Memos (encrypted metadata)
  async saveVoiceMemo(memo) {
    const { data, error } = await supabase
      .from('voice_memos')
      .insert([{
        name: memo.name,
        property_id: memo.propertyId,
        encrypted_data: memo.encryptedData,
        iv: memo.iv,
        type: memo.type,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getVoiceMemos(propertyId = null) {
    let query = supabase
      .from('voice_memos')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (propertyId) {
      query = query.eq('property_id', propertyId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Fozzil AI Conversations
  async saveConversation(message) {
    const { data, error } = await supabase
      .from('fozzil_conversations')
      .insert([{
        user_message: message.userMessage,
        ai_response: message.aiResponse,
        category: message.category,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getConversations(limit = 50) {
    const { data, error } = await supabase
      .from('fozzil_conversations')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  // Diary Entries
  async saveDiaryEntry(entry) {
    const { data, error } = await supabase
      .from('diary_entries')
      .insert([{
        title: entry.title,
        content: entry.content,
        author: entry.author,
        mood: entry.mood,
        tags: entry.tags,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getDiaryEntries(limit = 50) {
    const { data, error } = await supabase
      .from('diary_entries')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  async updateDiaryEntry(id, entry) {
    const { data, error } = await supabase
      .from('diary_entries')
      .update({
        title: entry.title,
        content: entry.content,
        mood: entry.mood,
        tags: entry.tags,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async deleteDiaryEntry(id) {
    const { error } = await supabase
      .from('diary_entries')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async searchDiaryEntries(searchTerm) {
    const { data, error } = await supabase
      .from('diary_entries')
      .select('*')
      .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Activity Logs - Track all user interactions
  async logActivity(activity) {
    const { data, error } = await supabase
      .from('activity_logs')
      .insert([{
        username: activity.username,
        action_type: activity.actionType,
        action_category: activity.actionCategory,
        action_details: activity.actionDetails || {},
        page_url: activity.pageUrl || window.location.href,
        session_id: activity.sessionId || sessionStorage.getItem('jk_session_id'),
        ip_address: activity.ipAddress || null,
        user_agent: activity.userAgent || navigator.userAgent,
        created_at: new Date().toISOString()
      }])
      .select();
    
    if (error) {
      console.error('Error logging activity:', error);
      return null;
    }
    return data[0];
  },

  async getUserActivityLogs(username, limit = 100) {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .eq('username', username)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  async getAllActivityLogs(limit = 100) {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  async getActivityLogsByCategory(category, limit = 100) {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .eq('action_category', category)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  },

  async getActivityStats(username = null) {
    let query = supabase
      .from('activity_logs')
      .select('action_type, action_category, created_at');
    
    if (username) {
      query = query.eq('username', username);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    // Process stats
    const stats = {
      total: data.length,
      byType: {},
      byCategory: {},
      byDate: {}
    };
    
    data.forEach(log => {
      // Count by type
      stats.byType[log.action_type] = (stats.byType[log.action_type] || 0) + 1;
      
      // Count by category
      if (log.action_category) {
        stats.byCategory[log.action_category] = (stats.byCategory[log.action_category] || 0) + 1;
      }
      
      // Count by date
      const date = new Date(log.created_at).toLocaleDateString();
      stats.byDate[date] = (stats.byDate[date] || 0) + 1;
    });
    
    return stats;
  },

  // Legacy Documents
  async saveDocument(document) {
    const { data, error } = await supabase
      .from('legacy_documents')
      .insert([{
        title: document.title,
        document_type: document.documentType,
        category: document.category,
        file_path: document.filePath,
        encrypted_data: document.encryptedData,
        auto_tagged: document.autoTagged || false,
        tags: document.tags || [],
        property_id: document.propertyId,
        upload_date: new Date().toISOString(),
        uploaded_by: document.uploadedBy
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getDocuments(category = null) {
    let query = supabase
      .from('legacy_documents')
      .select('*')
      .order('upload_date', { ascending: false });
    
    if (category) {
      query = query.eq('category', category);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Time Capsule Snapshots
  async saveTimeCapsule(snapshot) {
    const { data, error } = await supabase
      .from('time_capsules')
      .insert([{
        title: snapshot.title,
        description: snapshot.description,
        snapshot_date: snapshot.snapshotDate,
        milestone_type: snapshot.milestoneType,
        content_data: snapshot.contentData,
        media_urls: snapshot.mediaUrls || [],
        created_by: snapshot.createdBy
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getTimeCapsules() {
    const { data, error } = await supabase
      .from('time_capsules')
      .select('*')
      .order('snapshot_date', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Legacy Echo Triggers (AI Memory Prompts)
  async saveEchoTrigger(trigger) {
    const { data, error } = await supabase
      .from('echo_triggers')
      .insert([{
        phrase: trigger.phrase,
        context: trigger.context,
        emotion: trigger.emotion,
        trigger_conditions: trigger.triggerConditions,
        voice_sample_id: trigger.voiceSampleId,
        created_by: trigger.createdBy
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getEchoTriggers() {
    const { data, error } = await supabase
      .from('echo_triggers')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Interactive Artifacts
  async saveArtifact(artifact) {
    const { data, error } = await supabase
      .from('interactive_artifacts')
      .insert([{
        name: artifact.name,
        artifact_type: artifact.artifactType,
        description: artifact.description,
        media_url: artifact.mediaUrl,
        animation_data: artifact.animationData,
        sound_url: artifact.soundUrl,
        created_by: artifact.createdBy
      }])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getArtifacts() {
    const { data, error } = await supabase
      .from('interactive_artifacts')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  }
};
